import React, { MouseEvent, useMemo } from 'react'

import * as S from './styles'
import { ButtonIcon } from '../buttonIcon'
import { ChevronHorizontal } from '@/assets/icons'

export type BlogPaginationProps = {
  /**
   * Maximum number of elements before and after the centermost item
   * @example
   * ```tsx
   * <BlogPagination offset={2} />
   * ```
   * */
  offset?: number

  /**
   * Current index of pagination
   * @example
   * ```tsx
   * <BlogPagination index={1} />
   * ```
   * */
  currentPage?: number

  /**
   * Total number of pages
   * @example
   * ```tsx
   * <BlogPagination index={1} />
   * ```
   * */
  pageCount?: number

  /**
   * Function to handle page changes
   * @example
   * ```tsx
   * <BlogPagination setIndex={handleIndexChange} />
   * ```
   * */
  setCurrentPage: (index: number) => void
}

export const BlogPagination: React.FC<BlogPaginationProps> = ({
  offset = 2,
  currentPage = 1,
  pageCount = 1,
  setCurrentPage,
}) => {
  const pages = useMemo<number[]>(() => {
    const pagesArr = []
    // length is the sum of the two offsets on each side plus one centerpiece
    const length = 2 * offset + 1

    if (pageCount <= length) {
      for (let i = 1; i <= pageCount; i += 1) {
        pagesArr.push(i)
      }
    } else if (currentPage <= offset) {
      for (let i = 1; i <= length; i += 1) {
        pagesArr.push(i)
      }
    }
    // If current page is among the last
    else if (pageCount - currentPage < offset) {
      for (let i = pageCount - length + 1; i <= pageCount; i += 1) {
        pagesArr.push(i)
      }
      // Current page is neither one of the first or last items
    } else {
      // The current page should be the central element
      for (let i = currentPage - offset; i <= currentPage + offset; i += 1) {
        pagesArr.push(i)
      }
    }

    return [...pagesArr]
  }, [currentPage, offset, pageCount])

  const handleLeftClick = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const handleRightClick = () => {
    if (currentPage < pageCount) {
      setCurrentPage(currentPage + 1)
    }
  }

  const selectPage = (e: MouseEvent, page: number) => {
    e.preventDefault()
    setCurrentPage(page)
  }

  return (
    <S.InteractiveContainer>
      <S.ArrowLeft active={currentPage > 1}>
        <ButtonIcon
          icon={ChevronHorizontal}
          alt="chevron"
          onClick={handleLeftClick}
        />
      </S.ArrowLeft>

      <S.PaginationContainer>
        {pages.map((page) => (
          <button
            type="button"
            className={`pageButton ${page === currentPage ? 'selected' : ''}`}
            onClick={(e) => selectPage(e, page)}
            key={page}
          >
            {page}
          </button>
        ))}
      </S.PaginationContainer>

      <S.ArrowRight active={currentPage !== pageCount}>
        <ButtonIcon
          icon={ChevronHorizontal}
          alt="chevron"
          onClick={handleRightClick}
        />
      </S.ArrowRight>
    </S.InteractiveContainer>
  )
}
